import { ComponentChild, FunctionComponent, h } from 'preact';
import { Localizer } from 'preact-i18n';
import * as appStyles from '../../../../assets/global.scss';
import Icon from '../../../../common/Icon/Icon';
import { connectWithStyles } from '../../../../store';
import { Column, Row } from '../../../../common/Layout/Layout';
import { WithStyles } from '../../../../style';
import * as styles from './AmountField.scss';

export const buttonIds = {
  increase: (testIdSuffix: string): string => `amountField-add-${testIdSuffix}`,
  decrease: (testIdSuffix: string): string => `amountField-remove-${testIdSuffix}`,
};

interface StoreProps {
  enableCompactWidget: boolean;
}
export interface AmountFieldProps {
  label?: ComponentChild;
  helperText?: ComponentChild;
  amount: number;
  disableIncrease?: boolean;
  disableDecrease?: boolean;
  onIncrease: () => void;
  onDecrease: () => void;
  classes?: string;
  increaseAriaLabel?: ComponentChild;
  decreaseAriaLabel?: ComponentChild;
  testIdSuffix: string;
  autofocus?: boolean;
}

type EffectiveProps = AmountFieldProps & WithStyles & StoreProps;

const mapStateToProps = ({ enableCompactWidget }): StoreProps => ({
  enableCompactWidget,
});
export const AmountField: FunctionComponent<AmountFieldProps> = connectWithStyles<AmountFieldProps>(
  [styles, appStyles],
  {
    mapStateToProps,
  }
)(
  ({
    label,
    helperText,
    amount,
    classes,
    disableIncrease,
    disableDecrease,
    increaseAriaLabel,
    decreaseAriaLabel,
    testIdSuffix,
    autofocus,
    enableCompactWidget,
    onIncrease,
    onDecrease,
    tx,
  }: EffectiveProps) => {
    const onIncreaseClick = () => {
      if (!disableIncrease) {
        onIncrease();
      }
    };

    const onDecreaseClick = () => {
      if (!disableDecrease) {
        onDecrease();
      }
    };

    return (
      <Row class={tx(enableCompactWidget ? 'ps-xsmall-y' : 'ps-medium-y', classes)} grow={false}>
        <Column>
          <div class="font-body-1">{label}</div>
          {helperText && <div class="font-body-1 nordic-blue-900-text">{helperText}</div>}
        </Column>
        <Row class="flex--end" grow={false}>
          <Localizer>
            <button
              id={buttonIds.decrease(testIdSuffix)}
              class={tx('focusInsetOnIcon', disableDecrease ? 'nordic-blue-100-text' : 'nordic-blue-900-text')}
              aria-label={decreaseAriaLabel}
              onClick={onDecreaseClick}
              disabled={disableDecrease}
              autofocus={autofocus && amount > 1}
              data-test-amount-decrease
            >
              <Icon iconName="minus-outline" class={tx('is-medium overflowFocus', 'circle')} />
            </button>
          </Localizer>
          <span class={tx('selectionAmount', 'font-heading-3 nordic-blue-900-text align-center ps-medium-x')}>
            {amount}
          </span>
          <Localizer>
            <button
              id={buttonIds.increase(testIdSuffix)}
              class={tx('focusInsetOnIcon', disableIncrease ? 'nordic-blue-100-text' : 'nordic-blue-900-text')}
              aria-label={increaseAriaLabel}
              onClick={onIncreaseClick}
              disabled={disableIncrease}
              autofocus={autofocus && amount <= 1}
              data-test-amount-increase
            >
              <Icon iconName="plus-outline" class={tx('is-medium overflowFocus', 'circle')} />
            </button>
          </Localizer>
        </Row>
      </Row>
    );
  }
);
